'use client';

import { unstable_isUnrecognizedActionError } from 'next/navigation';

import { logCartEvent } from '@/lib/cart/logCartEvent';

let reloadScheduled = false;

/**
 * Reloads when a Server Action is rejected because the browser is running an
 * outdated build. Retrying cannot work: the action ID no longer exists on the
 * server. Next.js detects this condition but does not recover from it.
 * https://nextjs.org/docs/messages/failed-to-find-server-action
 *
 * Returns true once the caller should stop retrying, either because a reload
 * was scheduled or because retrying now would only fail the same way.
 */
export function recoverFromStaleServerAction(error, context = {}) {
  if (!unstable_isUnrecognizedActionError(error)) return false;
  if (reloadScheduled) return true;

  // Reloading after a reload would loop, an unavailable type is treated the
  // same. Fresh and history navigations can both serve a stale document.
  const navigationType = performance.getEntriesByType('navigation')[0]?.type;
  if (navigationType !== 'navigate' && navigationType !== 'back_forward') {
    // No reload here, but retrying would fail identically — see docblock.
    return true;
  }

  reloadScheduled = true;

  logCartEvent(
    'STALE_SERVER_ACTION_RELOAD',
    { ...context, error: error.message, url: window.location.href },
    'warn',
    { keepalive: true },
  );

  // Only an explicit reload revalidates the document instead of replaying the
  // cached copy behind this failure.
  window.location.reload();
  return true;
}
